import { useState, useEffect } from 'react';
import { useForm } from 'react-hook-form';
import { zodResolver } from '@hookform/resolvers/zod';
import { z } from 'zod';
import { useNavigate, useParams } from 'react-router-dom';
import toast from 'react-hot-toast';
import {
	ArrowLeftIcon,
	ExclamationTriangleIcon,
	UserIcon,
	InformationCircleIcon,
} from '@heroicons/react/24/outline';
import { useProvidersStore } from '../stores/providersStore';
import { AppLayout } from '../components/layout';
import { Card, Button, Input, Textarea, AppLoader } from '../components/ui';
import { providersApi, ApiError, getPrimaryLocation, type ProviderDetailResponse } from '../api';

// Validation schema
const providerSchema = z.object({
	businessName: z.string().min(1, 'Business name is required'),
	name: z.string().min(1, 'Provider name is required'),
	email: z.string().min(1, 'Email is required').email('Invalid email format'),
	phone: z
		.string()
		.min(1, 'Phone number is required')
		.regex(/^[\d\s\-()]+$/, 'Invalid phone number format'),
	address: z.string().min(1, 'Street address is required'),
	city: z.string().min(1, 'City is required'),
	state: z.string().min(1, 'State is required'),
	zipCode: z
		.string()
		.min(1, 'Zip code is required')
		.regex(/^\d{5}(-\d{4})?$/, 'Invalid zip code format (e.g., 12345 or 12345-6789)'),
	joinDate: z.string().optional(),
	bbpFee: z.number().min(0, 'BBP fee must be at least 0'),
	bookingEnabled: z.boolean(),
	accountStatus: z.boolean(),
	notes: z.string().optional(),
});

type ProviderFormData = z.infer<typeof providerSchema>;

export default function ProviderForm() {
	const { id } = useParams<{ id: string }>();
	const navigate = useNavigate();
	const cachedProviders = useProvidersStore(state => state.providers);
	const updateProviderInStore = useProvidersStore(state => state.updateProvider);
	const invalidateList = useProvidersStore(state => state.invalidateList);

	// Determine if we're in edit mode
	const isEditMode = !!id;
	const numericId = id ? parseInt(id, 10) : null;

	// Local state for fetched provider data
	const [provider, setProvider] = useState<ProviderDetailResponse | null>(null);
	const [isLoadingProvider, setIsLoadingProvider] = useState(false);
	const [loadError, setLoadError] = useState<string | null>(null);

	// Check cache first, then fetch from API if needed
	const cachedProvider = numericId ? cachedProviders.find(p => p.id === numericId) : null;

	// Fetch provider from API if in edit mode and not in cache
	useEffect(() => {
		if (isEditMode && numericId && !cachedProvider && !provider) {
			const fetchProvider = async () => {
				setIsLoadingProvider(true);
				setLoadError(null);
				try {
					const data = await providersApi.getById(numericId);
					setProvider(data);
				} catch (err) {
					setLoadError(err instanceof Error ? err.message : 'Failed to load provider');
				} finally {
					setIsLoadingProvider(false);
				}
			};
			fetchProvider();
		}
	}, [isEditMode, numericId, cachedProvider, provider]);

	// Use cached data or fetched data
	const providerData = cachedProvider || provider;

	// Get primary location for address fields
	const primaryLocation = providerData ? getPrimaryLocation(providerData.locations) : null;

	const {
		register,
		handleSubmit,
		watch,
		formState: { errors, isSubmitting },
	} = useForm<ProviderFormData>({
		resolver: zodResolver(providerSchema),
		defaultValues:
			isEditMode && providerData
				? {
						businessName: providerData.businessName,
						name: providerData.name,
						email: providerData.email,
						phone: providerData.phone,
						address: primaryLocation?.address || '',
						city: primaryLocation?.city || '',
						state: primaryLocation?.state || '',
						zipCode: primaryLocation?.zipCode || '',
						joinDate: providerData.joinedDate,
						bbpFee: providerData.bbpFee,
						bookingEnabled: providerData.bookingEnabled,
						accountStatus: providerData.accountStatus,
						notes: providerData.notes || '',
					}
				: {
						joinDate: new Date().toISOString().split('T')[0],
						bbpFee: 15,
						bookingEnabled: true,
						accountStatus: true,
					},
	});

	// Watch account status for the label
	const accountStatus = watch('accountStatus');

	const onSubmit = async (data: ProviderFormData) => {
		if (isEditMode) {
			// Edit mode - using real API
			if (!numericId || !providerData) return;

			try {
				const updatedProvider = await providersApi.update(numericId, {
					name: data.name,
					email: data.email,
					phone: data.phone,
					address: data.address,
					city: data.city,
					state: data.state,
					zipCode: data.zipCode,
					joinedDate: data.joinDate || providerData.joinedDate,
					bbpFee: data.bbpFee,
					bookingEnabled: data.bookingEnabled,
					notes: data.notes,
					businessName: data.businessName,
				});

				// Update the store with the response from API
				updateProviderInStore({
					...updatedProvider,
					accountStatus: data.accountStatus, // Include accountStatus from form
				});
				invalidateList(); // Trigger list refresh when navigating back to providers
				toast.success('Provider updated successfully!');
				navigate(`/providers/${numericId}`);
			} catch (error) {
				if (error instanceof ApiError) {
					// Use helper method or fallback to main message
					const fieldErrors = error.getFieldErrorMessages();
					toast.error(fieldErrors || error.message || 'Failed to update provider');
				} else {
					toast.error('Failed to update provider');
				}
			}
		} else {
			// Add mode - using real API (returns void)
			try {
				await providersApi.create({
					name: data.name,
					email: data.email,
					phone: data.phone,
					address: data.address,
					city: data.city,
					state: data.state,
					zipCode: data.zipCode,
					joinedDate: data.joinDate || new Date().toISOString().split('T')[0],
					bbpFee: data.bbpFee,
					bookingEnabled: data.bookingEnabled,
					notes: data.notes,
					businessName: data.businessName,
				});

				// API no longer returns provider data, so invalidate list and navigate
				invalidateList(); // Trigger list refresh to fetch new provider from API
				toast.success('Provider added successfully!');
				navigate('/providers');
			} catch (error) {
				if (error instanceof ApiError) {
					// Use helper method or fallback to main message
					const fieldErrors = error.getFieldErrorMessages();
					toast.error(fieldErrors || error.message || 'Failed to add provider');
				} else {
					toast.error('Failed to add provider');
				}
			}
		}
	};

	const handleCancel = () => {
		if (isEditMode) {
			navigate(`/providers/${id}`);
		} else {
			navigate('/providers');
		}
	};

	// Show loading state while fetching provider
	if (isEditMode && isLoadingProvider) {
		return (
			<AppLayout>
				<AppLoader message="Loading provider" variant="inline" />
			</AppLayout>
		);
	}

	// Show error state if failed to load provider
	if (isEditMode && loadError) {
		return (
			<AppLayout>
				<Card>
					<div className="text-center py-12">
						<div className="flex justify-center mb-4">
							<ExclamationTriangleIcon className="w-16 h-16 text-amber-500" />
						</div>
						<h2 className="text-2xl font-bold text-gray-900 dark:text-gray-100 mb-2">
							Failed to Load Provider
						</h2>
						<p className="text-gray-500 dark:text-gray-400 mb-6">{loadError}</p>
						<div className="flex justify-center gap-3">
							<Button variant="primary" onClick={() => window.location.reload()}>
								Try Again
							</Button>
							<Button variant="secondary" onClick={() => navigate('/providers')}>
								Back to Providers
							</Button>
						</div>
					</div>
				</Card>
			</AppLayout>
		);
	}

	// If in edit mode but provider not found (after loading), show not found
	if (isEditMode && !providerData && !isLoadingProvider) {
		return (
			<AppLayout>
				<Card>
					<div className="text-center py-12">
						<div className="flex justify-center mb-4">
							<UserIcon className="w-16 h-16 text-gray-300 dark:text-gray-600" />
						</div>
						<h2 className="text-2xl font-bold text-gray-900 dark:text-gray-100 mb-4">
							Provider Not Found
						</h2>
						<Button variant="primary" onClick={() => navigate('/providers')}>
							Back to Providers
						</Button>
					</div>
				</Card>
			</AppLayout>
		);
	}

	return (
		<AppLayout>
			<div className="max-w-[900px] mx-auto">
				{/* Back Link */}
				<button
					type="button"
					className="inline-flex items-center gap-1.5 text-blue-600 dark:text-blue-400 hover:text-blue-700 dark:hover:text-blue-300 mb-5 text-sm font-semibold transition-all hover:gap-2 cursor-pointer"
					onClick={handleCancel}
				>
					<ArrowLeftIcon className="w-4 h-4" />
					<span>{isEditMode ? 'Back to Provider Detail' : 'Back to Providers'}</span>
				</button>

				<Card>
					<h2 className="text-2xl font-bold mb-6 text-gray-900 dark:text-gray-100">
						{isEditMode ? `Edit Provider Profile` : 'Add New Provider'}
					</h2>

					<form onSubmit={handleSubmit(onSubmit)}>
						<div className="grid grid-cols-1 md:grid-cols-2 gap-5 mb-6">
							{/* Business Name */}
							<Input
								label="Business Name"
								placeholder="Enter business name"
								error={errors.businessName?.message}
								required
								disabled={isSubmitting}
								{...register('businessName')}
							/>

							{/* Provider Name */}
							<Input
								label="Provider Name"
								placeholder="Enter provider name"
								error={errors.name?.message}
								required
								disabled={isSubmitting}
								{...register('name')}
							/>

							{/* Email */}
							<Input
								type="email"
								label="Email"
								placeholder="Enter email"
								error={errors.email?.message}
								required
								disabled={isSubmitting}
								{...register('email')}
							/>

							{/* Phone */}
							<Input
								type="tel"
								label="Phone"
								placeholder="Enter phone number"
								error={errors.phone?.message}
								required
								disabled={isSubmitting}
								{...register('phone')}
							/>

							{/* Street Address - Full Width */}
							<div className="md:col-span-2">
								<Input
									label="Street Address"
									placeholder="Enter street address"
									error={errors.address?.message}
									required
									disabled={isSubmitting}
									{...register('address')}
								/>
							</div>

							{/* City */}
							<Input
								label="City"
								placeholder="Enter city"
								error={errors.city?.message}
								required
								disabled={isSubmitting}
								{...register('city')}
							/>

							{/* State */}
							<Input
								label="State"
								placeholder="Enter state"
								error={errors.state?.message}
								required
								disabled={isSubmitting}
								{...register('state')}
							/>

							{/* Zip Code */}
							<Input
								label="Zip Code"
								placeholder="Enter zip code"
								error={errors.zipCode?.message}
								required
								disabled={isSubmitting}
								{...register('zipCode')}
							/>

							{/* Join Date */}
							<Input
								type="date"
								label="Join Date"
								error={errors.joinDate?.message}
								disabled={isSubmitting}
								{...register('joinDate')}
							/>

							{/* BBP Fee */}
							<div className="md:col-span-2">
								<Input
									type="number"
									label="BBP Fee"
									placeholder="Enter fee amount"
									error={errors.bbpFee?.message}
									required
									min={0}
									step={0.1}
									disabled={isSubmitting}
									{...register('bbpFee', { valueAsNumber: true })}
								/>
								<p className="mt-1.5 text-xs text-gray-500 dark:text-gray-400 flex items-center gap-1.5">
									<InformationCircleIcon className="w-4 h-4 text-blue-600 dark:text-blue-400 shrink-0" />
									<span>
										The fee will automatically update based on the locations the
										provider adds.
									</span>
								</p>
							</div>

							{/* Booking Enabled - Full Width */}
							<div className="md:col-span-2">
								<label className="text-[13px] font-semibold text-gray-700 dark:text-gray-300 mb-2 block">
									Booking Enabled
								</label>
								<label className="toggle-switch">
									<input
										type="checkbox"
										{...register('bookingEnabled')}
										disabled={isSubmitting}
									/>
									<span className="toggle-slider"></span>
								</label>
							</div>

							{/* Account Status - Only shown in Edit mode */}
							{isEditMode && (
								<div className="md:col-span-2">
									<label className="text-[13px] font-semibold text-gray-700 dark:text-gray-300 mb-2 block">
										Account Status
									</label>
									<div className="flex items-center gap-3">
										<label className="toggle-switch">
											<input
												type="checkbox"
												{...register('accountStatus')}
												disabled={isSubmitting}
											/>
											<span className="toggle-slider"></span>
										</label>
										<span
											className={`text-sm font-semibold ${
												accountStatus ? 'text-green-500' : 'text-red-500'
											}`}
										>
											{accountStatus ? 'Active' : 'Inactive'}
										</span>
									</div>
								</div>
							)}

							{/* Notes - Full Width */}
							<div className="md:col-span-2">
								<Textarea
									label="Notes"
									placeholder="Enter any additional notes about the provider"
									error={errors.notes?.message}
									disabled={isSubmitting}
									rows={4}
									{...register('notes')}
								/>
							</div>
						</div>

						{/* Action Buttons */}
						<div className="flex gap-3 mt-7">
							<Button type="submit" variant="primary" isLoading={isSubmitting}>
								{isSubmitting
									? isEditMode
										? 'Saving...'
										: 'Adding...'
									: isEditMode
										? 'Save Changes'
										: 'Add Provider'}
							</Button>
							<Button
								type="button"
								variant="secondary"
								onClick={handleCancel}
								disabled={isSubmitting}
							>
								Cancel
							</Button>
						</div>
					</form>
				</Card>

				<div className="mt-10 text-center text-sm text-gray-400 dark:text-gray-500">
					© 2025 Beauty Business Portal. All rights reserved.
				</div>
			</div>
		</AppLayout>
	);
}
